'use client';

import useSWR, { Fetcher, SWRConfiguration } from 'swr';
import Endpoint from '../../endpoints';
import { ErrorResponse } from '../../error-response';
import { makeClientSideRequest } from '../http-client';

interface UseFetchResourceConfig<ResponseBody>
  extends SWRConfiguration<ResponseBody> {
  bypassErrorSnackbar?: boolean;
  bypassRedirectToLoginIfUnauthorized?: boolean;
  defaultErrorMessage?: string;
}

/**
 * Conditional fetching is supported by passing `null` as `body` preventing the request to be made.
 * ```
 * const {data: user} = useFetchResource(`...`);
 * const {data: settings} = useFetchResource(`...`, user != null ? {userId: user.id} : null);
 * ```
 * If your request `body` is `void`, don't pass a value or explicitly
 * pass `undefined` if passing `config` or if it was conditionally fetching.
 * ```
 * const [skip, setSkip] = useState(true);
 * const {data: noBody} = useFetchResource(`...`);
 * const {data: skipOrNoBody} = useFetchResource(`...`, skip ? null : undefined);
 * const {data: withConfig} = useFetchResource(`...`, undefined, {bypassErrorSnackbar: true});
 * ```
 */
export default function useFetchResource<RequestType, ResponseType>(
  endpoint: Endpoint,
  body?: RequestType | null,
  config?: UseFetchResourceConfig<ResponseType>,
) {
  const {
    bypassErrorSnackbar,
    defaultErrorMessage,
    bypassRedirectToLoginIfUnauthorized,
    ...swrConfig
  } = config || {};
  const fetcher: Fetcher<ResponseType, [Endpoint, RequestType]> = ([
    endpoint,
    body,
  ]: [Endpoint, RequestType]) =>
    makeClientSideRequest<RequestType, ResponseType>(endpoint, body, {
      bypassErrorSnackbar,
      bypassRedirectToLoginIfUnauthorized,
      defaultErrorMessage,
    });

  return useSWR<ResponseType, ErrorResponse>(
    body === null ? null : [endpoint, body],
    fetcher,
    swrConfig,
  );
}
